import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { AlertTriangle, CheckCircle2, Loader2, Shield } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { SmbAllowStep } from '../components/SmbAllowStep'
import { API_BASE_URL } from '../lib/api'

// DW-SMB-VERIFY-LANDING.
//
// The SMB signup email links to {FRONTEND_URL}/smb/verify?session=…&token=…
// (backend app/routers/smb_onboarding.py, ~L98-101). Before this page existed
// the SPA had no /smb/verify route, so the catch-all bounced the customer to
// the login screen and dropped the query — the signup could never be verified.
//
// This is a PUBLIC landing (the signer-up has no app account yet): it POSTs
// {API}/api/v1/smb-onboarding/verify with the two tokens. Confirming the
// email is no longer a full stop: a verified SMB session is not a login, but
// possession of the just-verified session token IS the capability to request
// consent — and a second-company admin arriving from the SMS link has no
// Principal yet, so the client home can never offer them the card. The
// organisation-access step (SmbAllowStep) is therefore offered right here on
// the verified pane. BRIEF-B12: while that allow step is showing it is the
// only call to action — the first-meeting line and the Sign in link are
// held back, and the pane says plainly what is next. They return only when
// the allow step renders nothing (consent refused or already done), exactly
// as before. No DW session is minted
// here, and this page itself never navigates anywhere — the Continue click
// lives inside SmbAllowStep. Any failure lands on a calm, honest error with
// no card and no consent call.
// The tokens are capability secrets: they go only in the request body (and
// sessionStorage for SmbAllowStep to redeem), never into visible copy.

export function SmbVerifyPage() {
  const [params] = useSearchParams()
  const session = params.get('session')
  const token = params.get('token')
  // A link missing either token can never verify — say so calmly and make
  // ZERO network calls (no probing the backend with half a credential).
  const missing = !session || !token

  const [failed, setFailed] = useState(false)
  const [verified, setVerified] = useState(false)
  // What SmbAllowStep (Microsoft or Google) reported once its consent
  // request settled: null while it is still loading, then true when it has
  // a card on screen and false when it renders nothing (refused, done, or
  // no session). While null the pane shows neither the Sign in link nor
  // the first-meeting line, so an owner on a slow connection is never
  // offered a button that is about to disappear.
  const [allowWillRender, setAllowWillRender] = useState<boolean | null>(null)
  // Effects can re-fire (StrictMode remount); verify must run exactly once.
  const startedRef = useRef(false)

  useEffect(() => {
    if (!session || !token || startedRef.current) return
    startedRef.current = true

    async function run(sessionToken: string, verifyToken: string) {
      try {
        // Unauthenticated by design — plain fetch, no Authorization header
        // (the shared axios client would attach any stored staff token).
        const verifyRes = await fetch(
          `${API_BASE_URL}/api/v1/smb-onboarding/verify`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              session_token: sessionToken,
              verify_token: verifyToken,
            }),
          },
        )
        if (verifyRes.ok) {
          // Persist the session token so SmbAllowStep — mounted below on the
          // verified pane, and again on the client home after the first
          // meeting — can redeem it against the consent endpoint.
          // A storage miss must never fail verification — swallow and go on.
          try {
            sessionStorage.setItem('dw_smb_session', sessionToken)
          } catch {
            // Storage unavailable: verification still stands.
          }
          setVerified(true)
        } else {
          setFailed(true)
        }
      } catch {
        setFailed(true)
      }
    }
    void run(session, token)
  }, [session, token])

  const showError = missing || failed

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-2xl border border-gray-200 p-8 text-center"
        role="status"
        aria-live="polite"
      >
        {showError ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-danger-light flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-danger" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-serif font-bold text-navy">
              We couldn’t verify this link
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              {missing
                ? 'This verification link is incomplete. Please open the most recent link from your signup email, or start the signup again.'
                : 'This link may have expired or already been used. Please start the signup again to receive a fresh link. Nothing has been set up yet.'}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Once your email is confirmed, the next step is your first
              meeting, and you’ll sign in for that.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center mt-6 tap-min px-5 py-2.5 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
            >
              Go to sign in
            </Link>
          </>
        ) : verified ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-brass/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-brass" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-serif font-bold text-navy">
              Your email is confirmed
            </h1>
            {allowWillRender === null ? (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mt-2">
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                Getting your next step ready…
              </div>
            ) : allowWillRender ? (
              <p className="text-sm text-gray-500 mt-2">
                Email confirmed. Next, give DigitalWatch read-only access.
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-500 mt-2">
                  Thanks, that’s done. Your next step is your first meeting,
                  where we’ll get you signed in and set up together.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center mt-6 tap-min px-5 py-2.5 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
                >
                  Sign in
                </Link>
              </>
            )}
            {/* Mounted only on the verified pane: SmbAllowStep redeems the
                session token just written above. On any consent failure or
                off-allow URL it renders NOTHING and the confirmed copy above
                stands untouched. */}
            <div className="mt-6 text-left">
              <SmbAllowStep onSettled={setAllowWillRender} />
            </div>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-2xl bg-brass/10 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-7 h-7 text-brass" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-serif font-bold text-navy">
              Confirming your email
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              One moment, we’re confirming your email address. Your first
              meeting is next.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mt-6">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Confirming…
            </div>
          </>
        )}
      </motion.div>
    </div>
  )
}
